import React, { useState } from 'react';
import { App, Block, Chip } from 'konsta/react';
import { useTranslation } from "react-i18next";

function Mood() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [chips, setChips] = useState([]);
  const [draggingChip, setDraggingChip] = useState(null);

  const CHIP_WIDTH = 10; // Approximate width of a chip in percentage
  const CHIP_HEIGHT = 5; // Approximate height of a chip in percentage
  const BUFFER = 2; // Minimum margin between chips in percentage

  const isOverlapping = (newPosition, existingPositions) => {
    return existingPositions.some((pos) => {
      const distanceX = Math.abs(newPosition.left - pos.left);
      const distanceY = Math.abs(newPosition.top - pos.top);
      return distanceX < CHIP_WIDTH + BUFFER && distanceY < CHIP_HEIGHT + BUFFER;
    });
  };

  const generateRandomPosition = () => {
    let newPosition;
    let attempts = 0;

    do {
      newPosition = {
        top: Math.random() * (100 - CHIP_HEIGHT - BUFFER), // Ensure chip stays within vertical bounds
        left: Math.random() * (100 - CHIP_WIDTH - BUFFER), // Ensure chip stays within horizontal bounds
      };
      attempts++;
    } while (isOverlapping(newPosition, chips) && attempts < 100); // Limit attempts to avoid infinite loops

    return newPosition;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      const randomPosition = generateRandomPosition();
      setChips((prevChips) => [
        ...prevChips,
        { text: searchQuery, position: randomPosition },
      ]);
      setSearchQuery(""); // Clear the input field
    }
  };

  const handleMouseDown = (index, e) => {
    e.preventDefault(); // Prevent default behavior (e.g., scrolling)
    setDraggingChip(index);
  };

  const handleMouseMove = (e) => {
    if (draggingChip !== null) {
      e.preventDefault(); // Prevent scrolling during dragging

      const newChips = [...chips];
      const newTop = ((e.touches ? e.touches[0].clientY : e.clientY) / window.innerHeight) * 100; // Handle touch or mouse
      const newLeft = ((e.touches ? e.touches[0].clientX : e.clientX) / window.innerWidth) * 100;

      newChips[draggingChip].position = {
        top: newTop,
        left: newLeft,
      };
      setChips(newChips);
    }
  };

  const handleMouseUp = (e) => {
    e.preventDefault(); // Prevent default behavior
    setDraggingChip(null);
  };

  // Add event listeners for touch events
  return (
    <App
      theme="ios"
      className="flex flex-col items-center justify-center h-screen relative"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchMove={handleMouseMove} // Handle touch move
    >
      <Block className="w-full max-w-lg mx-auto px-6">
        <div className="relative mb-6 group">
          <input
            type="text"
            placeholder={t("search.placeholder") || "Type your search..."}
            value={searchQuery}
            onChange={(e) => {
              e.stopPropagation(); // Prevent parent event interference
              setSearchQuery(e.target.value);
            }}
            onKeyDown={(e) => {
              e.stopPropagation(); // Prevent parent event interference
              handleKeyDown(e);
            }}
            className="w-full p-4 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-500 opacity-99 transition-all duration-500 ease-in-out transform group-hover:scale-105 focus:scale-105 group-hover:-translate-y-1 focus:-translate-y-1 group-hover:shadow-2xl focus:shadow-2xl"
          />
        </div>
      </Block>

      {chips.map((chip, index) => (
        <Chip
          key={index}
          className={`absolute cursor-pointer flex items-center group ${
            chip.isEditing ? "px-4" : "px-2"
          }`}
          style={{
            position: "absolute",
            top: `${chip.position.top}%`,
            left: `${chip.position.left}%`,
            transform: "translate(-50%, -50%)",
            userSelect: "none", // Prevent text selection while dragging
          }}
          onMouseDown={(e) => handleMouseDown(index, e)}
          onTouchStart={(e) => handleMouseDown(index, e)} // Handle touch start
        >
          <button
            className={`mr-2 ${
              chip.isEditing ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering drag logic
              const newChips = [...chips];
              newChips[index].isEditing = !newChips[index].isEditing; // Toggle editing mode
              setChips(newChips);
            }}
          >
            {chip.isEditing ? (
              // Check icon for completing editing
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-green-500 hover:text-green-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              // Slash icon for editing
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 20L20 4"
                />
              </svg>
            )}
          </button>
          {chip.isEditing ? (
            <input
              type="text"
              value={chip.text}
              onChange={(e) => {
                const newChips = [...chips];
                newChips[index].text = e.target.value;
                setChips(newChips);
              }}
              onFocus={(e) => {
                e.stopPropagation(); // Prevent triggering drag logic
                setDraggingChip(null); // Disable dragging while editing
              }}
              onMouseDown={(e) => {
                e.stopPropagation(); // Prevent triggering drag logic
              }}
              onTouchStart={(e) => {
                e.stopPropagation(); // Prevent triggering drag logic
              }}
              onBlur={() => {
                const newChips = [...chips];
                newChips[index].isEditing = false; // Exit editing mode
                setChips(newChips);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const newChips = [...chips];
                  newChips[index].isEditing = false; // Exit editing mode
                  setChips(newChips);
                }
              }}
              className="bg-transparent border-none focus:outline-none text-center"
            />
          ) : (
            <span>{chip.text}</span>
          )}
          <button
            className={`ml-2 ${
              chip.isEditing ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
            onClick={(e) => {
              e.stopPropagation(); // Prevent triggering drag logic
              setChips((prevChips) => prevChips.filter((_, i) => i !== index));
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </Chip>
      ))}
    </App>
  );
}

export default Mood;
